import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { localToday } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Check, X, CalendarDays, MessageCircle, UserCircle, Clock, Info, LogIn, LogOut, AlertCircle, Utensils, Bed, Baby, Plus, Trash2, BookOpen, DatabaseZap, Camera, MoreVertical, MoveRight } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import ChatDialog from "@/components/ChatDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import SchriftjeQuickDialog from "@/components/SchriftjeQuickDialog";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import { submitOrQueue } from "@/lib/offlineQueue";

function nowHHMM() {
  // Devicelokale tijd (browser) — expliciet gewenst door gebruiker.
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function PlanningPage() {
  const { user } = useAuth();
  const canWrite = can(user, "planning", "write");
  const canDelete = can(user, "planning", "delete");
  const [searchParams, setSearchParams] = useSearchParams();
  // Init datum uit URL (`?datum=YYYY-MM-DD`) — o.a. voor actie_link vanuit
  // Directie-Actiecentrum ('BKR morgen'). Fallback op vandaag.
  const _urlDatum = searchParams.get("datum");
  const _isValidDate = _urlDatum && /^\d{4}-\d{2}-\d{2}$/.test(_urlDatum);
  const [datum, setDatum] = useState(_isValidDate ? _urlDatum : localToday());
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatKindId, setChatKindId] = useState(null);
  const [infoKindId, setInfoKindId] = useState(null);
  const [schriftjeKind, setSchriftjeKind] = useState(null);
  const [photoKind, setPhotoKind] = useState(null);
  const [absentItem, setAbsentItem] = useState(null);
  const [moveItem, setMoveItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { datum };
      if (groupId !== "all") params.group_id = groupId;
      const r = await api.get("/planning/day", { params });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [datum, groupId]);

  useEffect(() => {
    api.get("/groups").then(r => setGroups(r.data));
  }, []);
  // Sync URL ?datum= met state (naast handmatige input) — zo overleven refreshes
  // en zijn deelbare links compatibel met de actie_link uit het Actiecentrum.
  useEffect(() => {
    const cur = searchParams.get("datum");
    if (cur !== datum) {
      const next = new URLSearchParams(searchParams);
      next.set("datum", datum);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datum]);
  useEffect(() => { load(); }, [load]);

  // Pedagogisch (leidster): dropdown strikt beperken tot eigen groep(en).
  const visibleGroups = user?.role === "pedagogisch" && user?.group_ids?.length
    ? groups.filter(g => user.group_ids.includes(g.id))
    : groups;

  const togglePresent = async (item) => { /* legacy, niet meer gebruikt */ };

  const aanmelden = async (item) => {
    if (!canWrite) return;
    try {
      const r = await submitOrQueue({
        url: "/planning/presentie",
        data: {
          kind_id: item.kind.id,
          group_id: item.group_id,
          datum,
          dagdeel: item.dagdeel,
          aanwezig: true,
          aankomst: nowHHMM(),
        },
        label: `Aanmelden ${item.kind.voornaam}`,
      });
      if (r.queued) toast.info(`${item.kind.voornaam} offline aangemeld — synct zodra je online bent`);
      else toast.success(`${item.kind.voornaam} aangemeld om ${nowHHMM()}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const afmelden = async (item) => {
    if (!canWrite) return;
    if (!item.presentie?.aankomst) {
      toast.error("Afmelden kan pas nadat het kind is aangemeld");
      return;
    }
    try {
      const r = await submitOrQueue({
        url: "/planning/presentie",
        data: {
          kind_id: item.kind.id,
          group_id: item.group_id,
          datum,
          dagdeel: item.dagdeel,
          aanwezig: false,
          vertrek: nowHHMM(),
        },
        label: `Afmelden ${item.kind.voornaam}`,
      });
      if (r.queued) toast.info(`${item.kind.voornaam} offline afgemeld — synct zodra je online bent`);
      else toast.success(`${item.kind.voornaam} afgemeld om ${nowHHMM()}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateTijd = async (item, field, value) => {
    if (!canWrite) return;
    if (value && !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
      toast.error("Tijd moet in HH:MM formaat"); return;
    }
    try {
      await api.post("/planning/presentie", {
        kind_id: item.kind.id,
        group_id: item.group_id,
        datum,
        dagdeel: item.dagdeel,
        aanwezig: item.presentie?.aanwezig ?? true,
        aankomst: field === "aankomst" ? value : (item.presentie?.aankomst || null),
        vertrek: field === "vertrek" ? value : (item.presentie?.vertrek || null),
      });
      toast.success(field === "aankomst" ? "Brengtijd bijgewerkt" : "Ophaaltijd bijgewerkt");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const quickLog = async (kindId, type, details = "") => {
    if (!canWrite) return;
    // Check aankomst voor dit kind vandaag
    const item = (data?.items || []).find(x => x.kind.id === kindId);
    if (!item?.presentie?.aankomst) {
      toast.error(`${item?.kind.voornaam || "Kind"} is nog niet aangemeld — meld het kind eerst aan.`);
      return;
    }
    try {
      const r = await submitOrQueue({
        url: "/daglog",
        data: { kind_id: kindId, type, details },
        label: `Daglog ${type}`,
      });
      if (r.queued) toast.info("Daglog offline opgeslagen — synct zodra je online bent");
      else toast.success(`${type.replace('_', ' ')} vastgelegd`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePresentie = async (item) => {
    if (!canDelete || !item.presentie?.id) return;
    const reason = window.prompt("Reden voor verwijdering (audit-log):", "");
    if (reason === null) return;
    if (!window.confirm(`Presentie-record van ${item.kind.voornaam} ${item.kind.achternaam} definitief verwijderen?`)) return;
    try {
      await api.delete(`/planning/presentie/${item.presentie.id}`, { params: { reason: reason || "" } });
      toast.success("Presentie-record verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const items = data?.items || [];
  const bkrList = data?.bkr || [];
  const isSuper = data?.is_super || user?.role === "superadmin" || user?.role === "platform_admin";
  const bkrByGroup = Object.fromEntries(bkrList.map(b => [b.group_id, b]));
  const byGroup = {};
  for (const it of items) {
    (byGroup[it.group_id] ||= []).push(it);
  }
  const groupMap = Object.fromEntries(groups.map(g => [g.id, g]));

  return (
    <div>
      <PageHeader
        title="Dagplanning & presentie"
        subtitle="Wie is er vandaag? Vink af per kind en dagdeel."
        testid="planning-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="date" value={datum} onChange={e => setDatum(e.target.value)} className="w-40" data-testid="planning-date" />
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger className="w-56" data-testid="planning-group"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Alle groepen</SelectItem>
                {visibleGroups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {(user?.role === "superadmin" || user?.role === "locatiemanager") && (
              <PresentieBackfillButton onDone={load} />
            )}
          </div>
        }
      />

      {loading && <div className="text-sm text-neutral-500">Planning laden…</div>}

      {/* WEBSTER KOV — Live-bezettings-balk. Compact horizontal, auto-refresh 30s.
          Toont per groep aanwezige-vs-vereiste PM's met kleurstatus. */}
      {!loading && bkrList.length > 0 && (
        <LiveBezettingBalk datum={datum} onReload={load} initialBkr={bkrList} />
      )}

      {!loading && items.length === 0 && (
        <Card className="p-12 bg-white shadow-none text-center">
          <CalendarDays className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
          <div className="text-sm text-neutral-600">Geen kinderen gepland op {new Date(datum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}.</div>
        </Card>
      )}

      {/* Live BKR-widget: real-time status per stamgroep — superadmin/locatiemanager */}
      {(user?.role === "superadmin" || user?.role === "locatiemanager" || user?.role === "platform_admin") && (
        <BKRLiveWidget datum={datum} />
      )}

      {/* BKR-overzicht — voor superadmin/locatiemanager als er meerdere groepen zijn */}
      {!loading && bkrList.length > 0 && (user?.role === "superadmin" || user?.role === "locatiemanager" || user?.role === "platform_admin") && groupId === "all" && (
        <Card className="p-4 bg-white shadow-none mb-6" data-testid="planning-bkr-overview">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-base tracking-tight">BKR-overzicht — alle groepen</h3>
              <p className="text-[11px] text-neutral-500">Indicatief · officiële berekening via <a className="underline" href="https://www.1ratio.nl" target="_blank" rel="noreferrer">1ratio.nl</a></p>
            </div>
            <div className="text-[11px] text-neutral-500">Op basis van dagplanning ({new Date(datum).toLocaleDateString('nl-NL')})</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {bkrList.map(b => (
              <div key={b.group_id} className={`p-2 rounded border text-xs ${b.status === 'ok' ? 'border-emerald-200 bg-emerald-50' : b.status === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`}>
                <div className="font-medium truncate">{b.name}</div>
                <div className="text-neutral-600">{b.kinderen} kind · {b.medewerkers_ingeroosterd}/{b.vereist} pm</div>
                <div className={`text-[10px] font-medium ${b.status === 'ok' ? 'text-emerald-700' : b.status === 'warning' ? 'text-amber-700' : 'text-red-700'}`}>{b.status === 'ok' ? 'Op sterkte' : b.status === 'warning' ? 'Krap' : 'Onder BKR'}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(byGroup).map(([gid, list]) => {
          const g = groupMap[gid] || {};
          // Aanwezig = aangekomen (aankomst gezet) én niet afgemeld
          const present = list.filter(x => x.presentie?.aankomst && x.presentie?.aanwezig !== false).length;
          const afgemeld = list.filter(x => x.presentie?.vertrek && x.presentie?.aanwezig === false).length;
          const nietAangekomen = list.length - present - afgemeld;
          const bkr = bkrByGroup[gid];
          const bkrTone = bkr && (bkr.status === "ok"
            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
            : bkr.status === "warning"
              ? "bg-amber-50 text-amber-800 border-amber-200"
              : "bg-red-50 text-red-800 border-red-200");
          return (
            <Card key={gid} className="p-6 bg-white shadow-none" data-testid={`planning-group-${gid}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display text-lg tracking-tight" style={{ color: g.color || "var(--sage)" }}>{g.name || "Onbekende groep"}</h3>
                  <div className="text-xs text-neutral-500">Capaciteit {g.capacity} · {list.length} ingepland · <span className="text-emerald-700 font-medium">{present} aangekomen</span>{afgemeld > 0 && <> · <span className="text-neutral-600">{afgemeld} opgehaald</span></>}{nietAangekomen > 0 && <> · <span className="text-amber-700">{nietAangekomen} nog niet</span></>}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant="outline" className="bg-[color:var(--sage-100)] text-[color:var(--sage)] border-transparent">
                    {list.length}/{g.capacity}
                  </Badge>
                  {bkr && (
                    <Badge variant="outline" className={`text-[10px] border ${bkrTone}`} data-testid={`bkr-badge-${gid}`}>
                      BKR: {bkr.medewerkers_ingeroosterd}/{bkr.vereist} pm · {bkr.status === "ok" ? "op sterkte" : bkr.status === "warning" ? "krap" : "onder"}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                {list.map(it => (
                  <PresentieRow
                    key={it.kind.id}
                    item={it}
                    canWrite={canWrite}
                    canDelete={canDelete}
                    isSuper={isSuper}
                    onAanmelden={aanmelden}
                    onAfmelden={afmelden}
                    onUpdateTijd={updateTijd}
                    onOpenChat={() => setChatKindId(it.kind.id)}
                    onOpenInfo={() => setInfoKindId(it.kind.id)}
                    onOpenSchriftje={() => setSchriftjeKind(it.kind)}
                    onQuickLog={quickLog}
                    onOpenPhoto={setPhotoKind}
                    onOpenAbsent={(it) => setAbsentItem({ ...it, datum })}
                    onOpenMove={(it) => setMoveItem({ ...it, datum })}
                    onDeletePresentie={deletePresentie}
                  />
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      <ChatDialog kindId={chatKindId} open={!!chatKindId} onClose={() => setChatKindId(null)} onSent={load} />
      <SchriftjeQuickDialog
        open={!!schriftjeKind}
        onClose={() => setSchriftjeKind(null)}
        kind={schriftjeKind}
      />
      <KindInfoDialog kindId={infoKindId} open={!!infoKindId}
        onClose={() => setInfoKindId(null)}
        onOpenChat={() => setChatKindId(infoKindId)} />

      <PhotoQuickDialog kind={photoKind} onClose={() => setPhotoKind(null)} />
      <AbsentDialog item={absentItem} onClose={() => setAbsentItem(null)} onDone={load} />
      <VerhuisDialog item={moveItem} groups={groups} onClose={() => setMoveItem(null)} onDone={load} />
    </div>
  );
}

/** Rij per kind — beheert lokale editable state voor brengtijd/ophaaltijd. Aanmelden en
 *  Afmelden zijn twee expliciete knoppen; afmelden is disabled als er geen aankomst is. */
function PresentieRow({ item, canWrite, canDelete, isSuper, onAanmelden, onAfmelden, onUpdateTijd, onOpenChat, onOpenInfo, onOpenSchriftje, onQuickLog, onOpenPhoto, onOpenAbsent, onOpenMove, onDeletePresentie }) {
  const p = item.presentie;
  const isAangemeld = !!p?.aankomst;
  const isAfgemeld = isAangemeld && p?.vertrek && !p?.aanwezig;
  const isAbsent = !!p?.absent_reason;
  const overrideName = item.group_override_name;
  const mentor = item.mentor;
  const schriftje = item.laatste_schriftje;
  // Leeftijd berekenen uit geboortedatum (YYYY-MM-DD)
  let ageLabel = "";
  if (item.kind.geboortedatum) {
    const gb = new Date(item.kind.geboortedatum);
    const now = new Date();
    let months = (now.getFullYear() - gb.getFullYear()) * 12 + (now.getMonth() - gb.getMonth());
    if (now.getDate() < gb.getDate()) months--;
    if (months < 24) ageLabel = `${months} mnd`;
    else ageLabel = `${Math.floor(months / 12)} jr`;
  }
  const [breng, setBreng] = useState(p?.aankomst || "");
  const [ophaal, setOphaal] = useState(p?.vertrek || "");
  useEffect(() => { setBreng(p?.aankomst || ""); }, [p?.aankomst]);
  useEffect(() => { setOphaal(p?.vertrek || ""); }, [p?.vertrek]);

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 rounded-md hover:bg-neutral-50 transition-colors" data-testid={`planning-kind-${item.kind.id}`}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)] font-semibold text-xs shrink-0">
        {item.kind.voornaam[0]}{item.kind.achternaam[0]}
      </div>
      <div className="flex-1 min-w-[160px]">
        <div className="text-sm font-medium text-neutral-800 truncate flex items-center gap-1.5 flex-wrap">
          {item.kind.voornaam} {item.kind.achternaam}
          {item.kind.geboortedatum && (
            <span className="text-[11px] text-neutral-400 font-normal" data-testid={`planning-gebdat-${item.kind.id}`}>
              ({new Date(item.kind.geboortedatum).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })})
            </span>
          )}
          {isAbsent && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" data-testid={`badge-absent-${item.kind.id}`}>Afwezig · {p.absent_reason}</Badge>}
          {overrideName && <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]" data-testid={`badge-verhuisd-${item.kind.id}`}>Vandaag in {overrideName}</Badge>}
          {isAfgemeld && !isAbsent && <Badge variant="outline" className="bg-neutral-100 text-neutral-600 text-[10px]">Afgemeld</Badge>}
          {isAangemeld && !isAfgemeld && !isAbsent && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Aangemeld</Badge>}
        </div>
        <div className="text-xs text-neutral-500 flex items-center gap-2 flex-wrap">
          <span className="capitalize">{item.dagdeel.replace('_', ' ')}</span>
          {ageLabel && (
            <>
              <span className="text-neutral-300">·</span>
              <span data-testid={`planning-leeftijd-${item.kind.id}`}>{ageLabel}</span>
            </>
          )}
          {mentor ? (
            <>
              <span className="text-neutral-300">·</span>
              <span className="flex items-center gap-1" data-testid={`planning-mentor-${item.kind.id}`}>
                <UserCircle className="h-3 w-3" />
                mentor: {mentor.voornaam} {mentor.achternaam}
              </span>
            </>
          ) : (
            <>
              <span className="text-neutral-300">·</span>
              <span className="flex items-center gap-1 text-amber-700" data-testid={`planning-mentor-missing-${item.kind.id}`}>
                <AlertCircle className="h-3 w-3" />
                geen mentor
              </span>
            </>
          )}
        </div>
        {schriftje && (
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-[color:var(--sage)]" data-testid={`planning-schriftje-preview-${item.kind.id}`} title={schriftje.activiteiten || schriftje.hoogtepunt || ""}>
            <BookOpen className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[220px]">
              Schriftje: {(schriftje.hoogtepunt || schriftje.activiteiten || "").slice(0, 60)}{(schriftje.hoogtepunt || schriftje.activiteiten || "").length > 60 && "…"}
            </span>
          </div>
        )}
      </div>

      {/* Tijd-inputs — admin/leidster kan corrigeren.
       *  Groene achtergrond = aanmeldtijd (aankomst), rode = afmeldtijd (vertrek). */}
      <div className="flex items-center gap-1.5" title="Brengtijd (aankomst) — admin/leidster kan corrigeren">
        <Clock className="h-3.5 w-3.5 text-emerald-700" />
        <Input
          type="time"
          value={breng}
          disabled={!canWrite}
          onChange={(e) => setBreng(e.target.value)}
          onBlur={() => { if ((breng || "") !== (p?.aankomst || "")) onUpdateTijd(item, "aankomst", breng); }}
          data-testid={`brengtijd-${item.kind.id}`}
          className={`w-[92px] h-8 text-xs font-medium ${breng ? "bg-emerald-50 border-emerald-300 text-emerald-800 focus-visible:ring-emerald-400" : "bg-white"}`}
        />
      </div>
      <div className="flex items-center gap-1.5" title="Ophaaltijd (vertrek)">
        <Clock className="h-3.5 w-3.5 text-red-600" />
        <Input
          type="time"
          value={ophaal}
          disabled={!canWrite}
          onChange={(e) => setOphaal(e.target.value)}
          onBlur={() => { if ((ophaal || "") !== (p?.vertrek || "")) onUpdateTijd(item, "vertrek", ophaal); }}
          data-testid={`ophaaltijd-${item.kind.id}`}
          className={`w-[92px] h-8 text-xs font-medium ${ophaal ? "bg-red-50 border-red-300 text-red-800 focus-visible:ring-red-400" : "bg-white"}`}
        />
      </div>

      <Button size="sm" variant="ghost" onClick={onOpenInfo}
        data-testid={`kind-info-${item.kind.id}`}
        className="text-neutral-600 hover:text-[color:var(--sage)]" title="Kind- en oudergegevens">
        <Info className="h-4 w-4" />
      </Button>

      <Button size="sm" variant="ghost"
        onClick={(e) => {
          if (!isAangemeld) {
            e.preventDefault();
            toast.error(`${item.kind.voornaam} is nog niet aangemeld — meld het kind eerst aan voor je een schriftje kunt maken.`);
            return;
          }
          onOpenSchriftje();
        }}
        data-testid={`schriftje-open-${item.kind.id}`}
        className={`text-neutral-600 hover:text-[color:var(--terracotta)] ${!isAangemeld ? "opacity-50" : ""}`}
        title={isAangemeld ? "Schriftje — dagverslag maken" : "Meld het kind eerst aan"}>
        <BookOpen className="h-4 w-4" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            disabled={!canWrite}
            data-testid={`quicklog-menu-${item.kind.id}`}
            className={`text-neutral-600 hover:text-[color:var(--sage)] ${!isAangemeld ? "opacity-50" : ""}`}
            title={isAangemeld ? "Snel loggen: maaltijd/slaap/activiteit" : "Meld het kind eerst aan"}
            onClick={(e) => {
              if (!isAangemeld) {
                e.preventDefault();
                toast.error(`${item.kind.voornaam} is nog niet aangemeld — meld het kind eerst aan voor je kunt loggen.`);
              }
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        {isAangemeld && (
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuItem data-testid={`quicklog-maaltijd-${item.kind.id}`} onClick={() => onQuickLog(item.kind.id, "maaltijd", "Fruithapje")}>
            <Utensils className="h-3.5 w-3.5 mr-2 text-amber-700" /> +Maaltijd
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`quicklog-slaap-start-${item.kind.id}`} onClick={() => onQuickLog(item.kind.id, "slaap_start")}>
            <Bed className="h-3.5 w-3.5 mr-2 text-blue-700" /> +Slaap start
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`quicklog-slaap-eind-${item.kind.id}`} onClick={() => onQuickLog(item.kind.id, "slaap_eind")}>
            <Bed className="h-3.5 w-3.5 mr-2 text-emerald-700" /> +Wakker
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`quicklog-activiteit-${item.kind.id}`} onClick={() => onQuickLog(item.kind.id, "activiteit", "Buitenspelen")}>
            <Baby className="h-3.5 w-3.5 mr-2 text-purple-700" /> +Activiteit
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`quicklog-foto-${item.kind.id}`}
            onSelect={(e) => { e.preventDefault(); onOpenPhoto?.(item.kind); }}
          >
            <Camera className="h-3.5 w-3.5 mr-2 text-[color:var(--terracotta)]" /> +Foto
          </DropdownMenuItem>
        </DropdownMenuContent>
        )}
      </DropdownMenu>

      <Button size="sm" variant="ghost" onClick={onOpenChat}
        data-testid={`chat-open-${item.kind.id}`}
        className="relative text-neutral-600 hover:text-[color:var(--sage)]" title="Chat met ouder">
        <MessageCircle className="h-4 w-4" />
        {item.unread_chat > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-[color:var(--terracotta)] text-white text-[10px] flex items-center justify-center px-1 font-semibold">
            {item.unread_chat}
          </span>
        )}
      </Button>

      {/* Twee expliciete knoppen: Aanmelden (groen) en Afmelden (rood), altijd naast elkaar. */}
      <Button
        size="sm"
        onClick={() => onAanmelden(item)}
        disabled={!canWrite || isAangemeld}
        data-testid={`aanmelden-btn-${item.kind.id}`}
        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 disabled:opacity-40 disabled:bg-emerald-600"
        title={isAangemeld ? "Kind is al aangemeld" : "Registreer brengtijd (nu)"}
      >
        <LogIn className="h-3.5 w-3.5 mr-1" /> Aanmelden
      </Button>
      <Button
        size="sm"
        onClick={() => onAfmelden(item)}
        disabled={!canWrite || !isAangemeld || isAfgemeld}
        data-testid={`afmelden-btn-${item.kind.id}`}
        className="bg-red-600 hover:bg-red-700 text-white h-8 disabled:opacity-40 disabled:bg-red-600"
        title={!isAangemeld ? "Eerst aanmelden vereist" : isAfgemeld ? "Kind is al afgemeld" : "Registreer ophaaltijd (nu)"}
      >
        <LogOut className="h-3.5 w-3.5 mr-1" /> Afmelden
      </Button>

      {/* Extra beheer-acties: absent-melden en verhuizen */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            disabled={!canWrite}
            data-testid={`row-actions-${item.kind.id}`}
            title="Extra acties: ziek melden, verhuizen"
            className="text-neutral-500 hover:text-neutral-800 h-8 px-2"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuItem
            data-testid={`row-mark-absent-${item.kind.id}`}
            onSelect={(e) => { e.preventDefault(); onOpenAbsent?.(item); }}
            disabled={isAangemeld || isAbsent}
          >
            <AlertCircle className="h-3.5 w-3.5 mr-2 text-amber-600" /> Afmelden (ziek/vakantie)
          </DropdownMenuItem>
          {isSuper && (
            <DropdownMenuItem
              data-testid={`row-move-${item.kind.id}`}
              onSelect={(e) => { e.preventDefault(); onOpenMove?.(item); }}
            >
              <MoveRight className="h-3.5 w-3.5 mr-2 text-sky-600" /> Verhuizen…
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {canDelete && item.presentie?.id && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDeletePresentie(item)}
          data-testid={`delete-presentie-${item.kind.id}`}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
          title="Superadmin: verwijder presentie-record (audit-gelogd)"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

/** Detail-dialog met kind- en oudergegevens. Backend filtert BSN/IBAN weg voor pedagogisch.
 *  Layout-prioriteit voor leidsters: (1) medisch/allergie bovenaan, (2) ophalers, (3) dagschema,
 *  (4) volledige kindgegevens, (5) chat-knop. */
function KindInfoDialog({ kindId, open, onClose, onOpenChat }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!kindId || !open) return;
    setInfo(null);
    setLoading(true);
    api.get(`/planning/kind/${kindId}/info`)
      .then(r => setInfo(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [kindId, open]);

  const k = info?.kind;
  const leeftijd = k?.geboortedatum ? Math.floor((Date.now() - new Date(k.geboortedatum).getTime()) / (365.25 * 24 * 3600 * 1000)) : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="kind-info-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {k ? `${k.voornaam} ${k.achternaam}` : "Kind-informatie"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Kind- en oudergegevens: medische bijzonderheden, wie mag ophalen, dagschema en chat.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="text-sm text-neutral-500">Laden…</div>}
        {!loading && info && (
          <div className="space-y-4" data-testid="kind-info-body">
            {/* MEDISCH BANNER — bovenaan, rood/oranje bij allergieën */}
            {info.has_medisch ? (
              <div className="p-4 rounded-md border-2 border-red-300 bg-red-50" data-testid="medisch-banner">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <div className="font-semibold text-red-800 uppercase text-xs tracking-widest">Medisch / Bijzonderheden</div>
                </div>
                <div className="space-y-1.5 text-sm">
                  {info.medisch.allergies && (
                    <div data-testid="medisch-allergies">
                      <span className="font-semibold text-red-800">Allergieën:</span> {info.medisch.allergies}
                    </div>
                  )}
                  {info.medisch.notes && (
                    <div><span className="font-semibold text-red-800">Bijzonderheden:</span> {info.medisch.notes}</div>
                  )}
                  {info.medisch.huisarts && (
                    <div><span className="font-semibold text-red-800">Huisarts:</span> {info.medisch.huisarts}</div>
                  )}
                  {info.medisch.tandarts && (
                    <div><span className="font-semibold text-red-800">Tandarts:</span> {info.medisch.tandarts}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 text-xs text-emerald-800 flex items-center gap-2" data-testid="medisch-banner-empty">
                <Check className="h-4 w-4" /> Geen medische bijzonderheden geregistreerd
              </div>
            )}

            {/* KIND-GEGEVENS (kort) */}
            <section>
              <h3 className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Kind</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <Field label="Geboortedatum" value={k?.geboortedatum} />
                <Field label="Leeftijd" value={leeftijd != null ? `${leeftijd} jaar` : "—"} />
                <Field label="Groep" value={info.groep?.name} />
                <Field label="Locatie" value={info.locatie?.name} />
                <Field label="Mentor" value={info.mentor ? `${info.mentor.voornaam} ${info.mentor.achternaam}` : "—"} />
                <Field label="Startdatum" value={k?.startdatum} />
              </div>
            </section>

            {/* WIE MAG OPHALEN */}
            <section data-testid="ophalers-section">
              <h3 className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                Wie mag ophalen — {info.ophalers?.length || 0}
              </h3>
              <div className="space-y-2">
                {info.ophalers?.map((o, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-md border border-neutral-200 bg-neutral-50/50" data-testid={`ophaler-${i}`}>
                    <div>
                      <div className="font-medium text-neutral-800 text-sm">{o.naam}</div>
                      <div className="text-[11px] text-neutral-500">{o.relatie}</div>
                    </div>
                    {o.telefoon && (
                      <a href={`tel:${o.telefoon}`} className="text-sm text-[color:var(--sage)] font-medium hover:underline">
                        {o.telefoon}
                      </a>
                    )}
                  </div>
                ))}
                {(!info.ophalers || info.ophalers.length === 0) && (
                  <div className="text-sm text-neutral-500">Geen ophalers geregistreerd.</div>
                )}
              </div>
            </section>

            {/* VERHUIS-HISTORIE (alleen superadmin) */}
            <VerhuisHistorySection kindId={kindId} />

            {/* CHAT-ACTIE */}
            {onOpenChat && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { onOpenChat(); onClose(); }}
                  data-testid="kind-info-chat-btn"
                  className="border-[color:var(--sage)] text-[color:var(--sage)] hover:bg-[color:var(--sage-100)]"
                >
                  <MessageCircle className="h-4 w-4 mr-1.5" />
                  Chat met ouder
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, full }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-sm text-neutral-800">{value || "—"}</div>
    </div>
  );
}


function VerhuisHistorySection({ kindId }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const isSuper = user?.role === "superadmin" || user?.role === "platform_admin";
  useEffect(() => {
    if (!kindId || !isSuper) { setRows(null); return; }
    api.get(`/planning/verhuis-log?kind_id=${kindId}`)
      .then(r => setRows(r.data.items || []))
      .catch(() => setRows([]));
  }, [kindId, isSuper]);

  if (!isSuper) return null;

  return (
    <section data-testid="verhuis-history-section">
      <h3 className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
        Verhuis-historie {rows && `— ${rows.length}`}
      </h3>
      {rows === null && <div className="text-xs text-neutral-400">Laden…</div>}
      {rows && rows.length === 0 && (
        <div className="text-sm text-neutral-500">Geen verhuizingen geregistreerd.</div>
      )}
      {rows && rows.length > 0 && (
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {rows.map(r => (
            <div key={r.id} className="p-2 rounded-md border border-neutral-200 bg-neutral-50/50 text-xs" data-testid={`verhuis-history-row-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] ${r.mode === "permanent" ? "bg-red-50 text-red-700 border-red-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                    {r.mode}
                  </Badge>
                  <span className="font-medium text-neutral-800">{r.van_group_naam} → {r.naar_group_naam}</span>
                </div>
                <span className="text-[10px] text-neutral-400 font-mono">{r.created_at?.slice(0, 16).replace('T', ' ')}</span>
              </div>
              <div className="mt-1 text-neutral-600 flex flex-wrap gap-x-3 gap-y-0.5">
                {r.datum && <span>Datum: <strong>{r.datum}</strong></span>}
                {r.dagdelen?.length > 0 && <span>Dagdelen: {r.dagdelen.join(", ")}</span>}
                <span>Door: {r.door_naam}</span>
              </div>
              {r.reden && (
                <div className="mt-1 text-[11px] text-neutral-500 italic">Reden: "{r.reden}"</div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function BKRLiveWidget({ datum }) {
  const { user } = useAuth();
  const currentRole = user?.role;
  const [data, setData] = useState(null);
  const [ticking, setTicking] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/bkr/live", { params: { datum } });
      setData(r.data);
    } catch { /* ignore silent */ }
  }, [datum]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30_000); // refresh 30s
    return () => clearInterval(iv);
  }, [load]);

  const tick = async () => {
    setTicking(true);
    try {
      const r = await api.post("/bkr/live/tick");
      if (r.data.changes > 0) toast.success(`${r.data.changes} timer(s) bijgewerkt`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTicking(false); }
  };

  if (!data || (data.items || []).length === 0) return null;

  // 'flags' was een half-afgemaakte R3-badge (regel 3: aangemelde kinderen
  // zonder aanwezigheidsrecord). Backend levert dit veld nog niet, dus we
  // defaulten naar een leeg object zodat de widget niet crasht.
  const flags = data.flags || {};

  const tone = (s) => ({
    ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
    afwijk_gepland: "border-amber-200 bg-amber-50 text-amber-800",
    afwijk_ongepland: "border-orange-300 bg-orange-50 text-orange-800",
    overtreding: "border-red-300 bg-red-50 text-red-800",
    overstaffed: "border-purple-300 bg-purple-50 text-purple-800",
  }[s] || "border-neutral-200 bg-neutral-50 text-neutral-700");

  const label = (s) => ({
    ok: "Op sterkte",
    afwijk_gepland: "Afwijking (gepland)",
    afwijk_ongepland: "Afwijking (ongepland)",
    overtreding: "Overtreding",
    overstaffed: "⬆ Meer PM dan vereist",
  }[s] || s);

  return (
    <Card className="p-4 bg-white shadow-none mb-4" data-testid="bkr-live-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-display text-base tracking-tight flex items-center gap-2">
            <Clock className="h-4 w-4 text-[color:var(--sage)]" />
            Live BKR — real-time
          </h3>
          <p className="text-[11px] text-neutral-500">
            Op basis van daadwerkelijk aangekomen kinderen + aanwezige medewerkers. Ververst elke 30 seconden.
            Max {data.wettelijk_max_min} min/dag/groep afwijking (drie-uursregeling).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={tick} disabled={ticking} data-testid="bkr-live-tick">
          {ticking ? "Timer…" : "Update timers"}
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {(data.items || []).map(it => (
          <div key={it.group_id} className={`p-2 rounded border text-xs ${tone(it.status)}`} data-testid={`bkr-live-${it.group_id}`}>
            <div className="font-medium truncate flex items-center gap-1">
              {it.group_name}
              {flags[it.group_id] && (
                <span title={`Regel 3: ${flags[it.group_id]} aangemelde kind(eren) zonder record — controleer presentie`}
                      data-testid={`r3-flag-${it.group_id}`}
                      className="text-red-600 cursor-help">🚩</span>
              )}
            </div>
            <div>{it.kinderen_aangekomen} kind aanwezig · {it.medewerkers_aanwezig}/{it.vereist} pm</div>
            <div className="font-medium text-[10px] mt-0.5">{label(it.status)}</div>
            {flags[it.group_id] && (
              <a href={`/app/presentielijst?group_id=${it.group_id}&datum=${datum}&filter=missing`}
                 data-testid={`r3-fix-${it.group_id}`}
                 className="inline-block text-[10px] text-red-700 underline mt-1 hover:text-red-900">
                Fix nu →
              </a>
            )}
            {(it.status === "afwijk_ongepland" || it.status === "overtreding") && ["locatiemanager","superadmin"].includes(currentRole) && (
              <BKRAISuggestButton groupId={it.group_id} datum={datum} groupName={it.group_name} />
            )}
            {(it.afwijk_werkelijk_min > 0 || it.afwijk_actief) && (
              <div className={`text-[10px] mt-0.5 ${it.afwijk_werkelijk_min >= 165 ? "text-red-700 font-semibold" : ""}`}>
                Afwijking: {it.afwijk_werkelijk_min}/{data.wettelijk_max_min} min{it.afwijk_actief && " · loopt"}
              </div>
            )}
            {it.waarschuwing_min && (
              <div className="text-[10px] text-red-700 font-semibold mt-0.5">
                ⚠ {it.waarschuwing_min} min bereikt
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}



/** Presentie-backfill dialog: genereer aanwezigheids-records op basis van
 * kindcontracten (of platte werkdagen) zodat BKR-controle echte kind-aantallen krijgt. */
function PresentieBackfillButton({ onDone }) {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    from_date: iso(monday),
    to_date: iso(friday),
    mode: "contract_days",
    aankomst: "08:30",
    vertrek: "17:30",
    overwrite: false,
  });
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/presentie/backfill", form);
      setResult(data);
      toast.success(`Backfill klaar · ${data.created} nieuw · ${data.updated} bijgewerkt · ${data.skipped} overgeslagen`);
      onDone?.();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Backfill mislukt";
      toast.error(msg);
      setResult({ error: msg });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="presentie-backfill-btn" title="Vul presentie voor een periode uit contract-dagen zodat BKR echte aantallen gebruikt">
        <DatabaseZap className="h-4 w-4 mr-1" /> Backfill presentie
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle>Presentie backfillen</DialogTitle>
            <DialogDescription>
              Genereer aanwezigheids-records voor een periode. Standaard-modus gebruikt de
              contract-dagen van elk kind, zodat de BKR-controle exact klopt.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Van datum</Label>
              <Input type="date" value={form.from_date} onChange={e => setForm({ ...form, from_date: e.target.value })} data-testid="backfill-from" />
            </div>
            <div>
              <Label>Tot en met</Label>
              <Input type="date" value={form.to_date} onChange={e => setForm({ ...form, to_date: e.target.value })} data-testid="backfill-to" />
            </div>
            <div className="col-span-2">
              <Label>Modus</Label>
              <Select value={form.mode} onValueChange={v => setForm({ ...form, mode: v })}>
                <SelectTrigger data-testid="backfill-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="contract_days">Volg contract-dagen (aanbevolen)</SelectItem>
                  <SelectItem value="flat_weekdays">Alle werkdagen (ma–vr) present</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-neutral-500 mt-1">
                {form.mode === "contract_days"
                  ? "Alleen dagen die in het actieve contract van elk kind staan (dag_ma..dag_zo)."
                  : "Elke werkdag markeert álle kinderen als present (voor snelle test-scenario's)."}
              </div>
            </div>
            <div>
              <Label>Standaard brengtijd</Label>
              <Input type="time" value={form.aankomst} onChange={e => setForm({ ...form, aankomst: e.target.value })} data-testid="backfill-aankomst" />
            </div>
            <div>
              <Label>Standaard ophaaltijd</Label>
              <Input type="time" value={form.vertrek} onChange={e => setForm({ ...form, vertrek: e.target.value })} data-testid="backfill-vertrek" />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-xs text-neutral-700">
              <Checkbox checked={form.overwrite} onCheckedChange={c => setForm({ ...form, overwrite: !!c })} data-testid="backfill-overwrite" />
              Overschrijf bestaande records (anders skipt bestaande records)
            </label>
          </div>
          {result && !result.error && (
            <div className="mt-3 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-xs text-emerald-900" data-testid="backfill-result">
              ✓ {result.created} nieuw · {result.updated} bijgewerkt · {result.skipped} overgeslagen
              <div className="text-[11px] text-emerald-800 mt-1">
                Periode: {result.days} dagen · Kinderen in scope: {result.kinderen} · Modus: {result.mode}
              </div>
            </div>
          )}
          {result?.error && (
            <div className="mt-3 p-3 rounded-md border border-red-200 bg-red-50 text-xs text-red-800">{result.error}</div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Sluiten</Button>
            <Button
              onClick={run}
              disabled={busy}
              data-testid="backfill-run"
              style={{ background: "var(--sage)" }}
              className="text-white"
            >
              {busy ? "Bezig…" : "Backfill starten"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/** Klein foto-dialoogje dat door de +Foto quicklog-actie geopend wordt.
 *  Uploadt direct via PhotoUploadButton — de knop opent zelf de camera. */
function PhotoQuickDialog({ kind, onClose }) {
  if (!kind) return null;
  return (
    <Dialog open={!!kind} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm bg-white" data-testid="photo-quick-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-[color:var(--terracotta)]" />
            Foto voor {kind.voornaam}
          </DialogTitle>
          <DialogDescription>
            Kies of maak een foto. Deze wordt opgeslagen bij {kind.voornaam}.
            Zonder toestemming van de ouder wordt de foto geweigerd.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <PhotoUploadButton
            kindIds={[kind.id]}
            label="Foto kiezen of maken"
            variant="default"
            size="default"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="photo-quick-close">Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/** Dialog om een kind ziek/vakantie te melden voor de rest van de dag. */
function AbsentDialog({ item, onClose, onDone }) {
  const [reden, setReden] = useState("ziek");
  const [busy, setBusy] = useState(false);
  if (!item) return null;
  const kind = item.kind;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/planning/absent", {
        kind_id: kind.id,
        datum: item.datum || new Date().toISOString().slice(0, 10),
        reden,
        dagdelen: [item.dagdeel],
      });
      toast.success(`${kind.voornaam} is afgemeld (${reden})`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm bg-white" data-testid="absent-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            {kind.voornaam} afmelden voor vandaag
          </DialogTitle>
          <DialogDescription>
            Het kind wordt vandaag als afwezig gemarkeerd. De ouder en team zien dit direct.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-neutral-600">Reden</label>
            <select
              value={reden}
              onChange={e => setReden(e.target.value)}
              data-testid="absent-reden"
              className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1"
            >
              <option value="ziek">Ziek</option>
              <option value="vakantie">Vakantie</option>
              <option value="afwezig">Afwezig</option>
              <option value="doktersbezoek">Doktersbezoek</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} data-testid="absent-submit"
            className="bg-amber-600 hover:bg-amber-700 text-white">
            {busy ? "Bezig…" : "Afmelden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dialog om een kind te verhuizen naar een andere groep. */
function VerhuisDialog({ item, groups, onClose, onDone }) {
  const [naar, setNaar] = useState("");
  const [mode, setMode] = useState("tijdelijk");
  const [reden, setReden] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (item) { setNaar(""); setMode("tijdelijk"); setReden(""); setPreview(null); }
  }, [item]);
  // BKR-impact preview zodra doelgroep gekozen is
  useEffect(() => {
    if (!item || !naar || mode !== "tijdelijk") { setPreview(null); return; }
    const datumStr = item.datum || new Date().toISOString().slice(0, 10);
    api.post("/planning/verhuis/preview", {
      kind_id: item.kind.id, naar_group_id: naar, datum: datumStr,
    }).then(r => setPreview(r.data)).catch(() => setPreview(null));
  }, [item, naar, mode]);

  if (!item) return null;
  const kind = item.kind;
  const currentGroup = item.group_id;
  const datumStr = item.datum || new Date().toISOString().slice(0, 10);
  const datumLabel = new Date(datumStr).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const isPast = datumStr < new Date().toISOString().slice(0, 10);
  const options = (groups || []).filter(g => g.location_id === kind.location_id && g.id !== currentGroup);

  const submit = async () => {
    if (!naar) { toast.error("Kies een doelgroep"); return; }
    if (preview?.breach && !reden.trim()) {
      toast.error("Deze verhuis zou de BKR overtreden — geef een reden op");
      return;
    }
    setBusy(true);
    try {
      await api.post("/planning/verhuis", {
        kind_id: kind.id,
        naar_group_id: naar,
        mode,
        datum: datumStr,
        dagdelen: [item.dagdeel],
        reason: reden || undefined,
      });
      const targetName = options.find(o => o.id === naar)?.name || "doelgroep";
      toast.success(mode === "permanent"
        ? `${kind.voornaam} permanent verhuisd naar ${targetName}`
        : `${kind.voornaam} op ${datumLabel} in ${targetName}`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const impactColor = (voor, na) => {
    if (na.status === "alert" && voor.status !== "alert") return "border-red-300 bg-red-50 text-red-800";
    if (na.status === "warning" && voor.status === "ok") return "border-amber-300 bg-amber-50 text-amber-800";
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="verhuis-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MoveRight className="h-4 w-4 text-sky-600" />
            {kind.voornaam} verhuizen
          </DialogTitle>
          <DialogDescription>
            {mode === "permanent"
              ? "Permanente verhuizing past de groep aan én actieve contracten."
              : (
                <span>
                  Tijdelijk op <strong>{datumLabel}</strong>{isPast && <span className="text-amber-700"> (met terugwerkende kracht)</span>}. Facturering blijft volgens contract.
                </span>
              )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-neutral-600">Naar welke groep?</label>
            <select
              value={naar}
              onChange={e => setNaar(e.target.value)}
              data-testid="verhuis-naar"
              className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1"
            >
              <option value="">— kies groep —</option>
              {options.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-neutral-600 shrink-0">Type</label>
            <div className="flex gap-1">
              <button type="button" onClick={() => setMode("tijdelijk")}
                data-testid="verhuis-tijdelijk"
                className={`px-2 py-1 text-xs rounded ${mode === "tijdelijk" ? "bg-sky-600 text-white" : "bg-neutral-100 text-neutral-600"}`}>
                Tijdelijk ({new Date(datumStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })})
              </button>
              <button type="button" onClick={() => setMode("permanent")}
                data-testid="verhuis-permanent"
                className={`px-2 py-1 text-xs rounded ${mode === "permanent" ? "bg-sky-600 text-white" : "bg-neutral-100 text-neutral-600"}`}>
                Permanent
              </button>
            </div>
          </div>

          {/* BKR-impact preview */}
          {preview && (
            <div className="space-y-2" data-testid="verhuis-preview">
              {preview.breach && (
                <div className="p-2 rounded border border-red-300 bg-red-50 text-red-800 text-xs" data-testid="verhuis-breach-warning">
                  <div className="font-semibold flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> Waarschuwing — deze verhuis breekt de BKR</div>
                  <div className="mt-1">Doelgroep <strong>{preview.doel.name}</strong> zou van "{preview.doel.voor.status}" naar "{preview.doel.na.status}" gaan. Vermeld hieronder een reden om toch door te gaan.</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className={`p-2 rounded border ${impactColor(preview.bron.voor, preview.bron.na)}`}>
                  <div className="font-medium">{preview.bron.name} (bron)</div>
                  <div>{preview.bron.voor.kinderen} → <strong>{preview.bron.na.kinderen}</strong> kind</div>
                  <div>BKR: {preview.bron.na.medewerkers_ingeroosterd}/{preview.bron.na.vereist} pm</div>
                </div>
                <div className={`p-2 rounded border ${impactColor(preview.doel.voor, preview.doel.na)}`}>
                  <div className="font-medium">{preview.doel.name} (doel)</div>
                  <div>{preview.doel.voor.kinderen} → <strong>{preview.doel.na.kinderen}</strong> kind</div>
                  <div>BKR: {preview.doel.na.medewerkers_ingeroosterd}/{preview.doel.na.vereist} pm</div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-neutral-600">Reden {preview?.breach && <span className="text-red-600">*verplicht bij BKR-breach</span>}</label>
            <Input value={reden} onChange={e => setReden(e.target.value)} placeholder="bv. leidster ziek in eigen groep" data-testid="verhuis-reden" />
          </div>

          <div className="text-[10px] text-neutral-400 pt-1 border-t border-neutral-100">
            Alleen superadmin ziet dat er een tijdelijke verhuis heeft plaatsgevonden. Overige rollen zien de dagplanning zonder markers.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} data-testid="verhuis-submit"
            className="bg-sky-600 hover:bg-sky-700 text-white">
            {busy ? "Bezig…" : "Verhuizen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BKRAISuggestButton({ groupId, datum, groupName }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bkr/ai-suggest", { group_id: groupId, datum });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI-suggestie mislukt");
    }
    setBusy(false);
  };

  return (
    <>
      <button onClick={(e) => { e.preventDefault(); setOpen(true); load(); }}
        data-testid={`ai-bkr-btn-${groupId}`}
        className="block mt-1 text-[10px] text-purple-700 underline hover:text-purple-900">
        ✨ AI-suggestie
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="ai-bkr-dialog">
          <DialogHeader>
            <DialogTitle>AI-suggesties voor {groupName}</DialogTitle>
            <DialogDescription>Concrete acties om de BKR-situatie vandaag ({datum}) op te lossen.</DialogDescription>
          </DialogHeader>
          {busy ? (
            <div className="text-center py-6 text-neutral-500 text-sm">AI denkt na…</div>
          ) : result ? (
            <div className="space-y-2">
              {(result.suggestions || []).map((s, i) => (
                <div key={i} className="p-3 rounded border border-purple-200 bg-purple-50" data-testid={`ai-sug-${i}`}>
                  <div className="text-xs font-medium mb-1 flex items-center gap-1">
                    <span className="px-1.5 py-0.5 rounded bg-purple-200 text-purple-900 text-[9px] uppercase">
                      {s.type || "actie"}
                    </span>
                    {s.titel}
                  </div>
                  <div className="text-xs text-neutral-700">{s.actie}</div>
                  {s.type === "inroosteren" && <OproepButton groupId={groupId} datum={datum} reden={`${s.titel}: ${s.actie}`} />}
                </div>
              ))}
              {result.context && (
                <div className="text-[10px] text-neutral-500 pt-2 border-t">
                  Context: {result.context.aanwezige_kinderen}/{result.context.totaal_kinderen} kinderen ·
                  {" "}{result.context.diensten_vandaag} diensten · {result.context.andere_groepen} andere groepen
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-neutral-400 text-sm">Klik hierboven om te laden</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function OproepButton({ groupId, datum, reden }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const call = async () => {
    setBusy(true);
    try {
      await api.post("/personeel/oproepen", { group_id: groupId, datum, reden });
      setDone(true);
      toast.success("Oproeptaak aangemaakt in personeelsapp");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Oproep mislukt");
    }
    setBusy(false);
  };
  return (
    <button onClick={call} disabled={busy || done} data-testid="oproep-btn"
      className={`mt-2 text-[10px] px-2 py-1 rounded font-medium ${
        done ? "bg-emerald-100 text-emerald-800" : "bg-purple-600 text-white hover:bg-purple-700"
      }`}>
      {done ? "✓ Taak aangemaakt" : busy ? "Bezig…" : "📞 Roep extra pm op"}
    </button>
  );
}

/**
 * WEBSTER KOV — Live-bezettings-balk
 *
 * Compacte horizontale balk direct onder de PageHeader die per groep laat zien
 * hoeveel PM's aanwezig zijn versus vereist volgens BKR. Auto-refresh elke 30s
 * zodat directie in één oogopslag ziet waar het knelt.
 *
 * Bron: /api/planning/day (unified op db.rooster sinds P0-fix feb 2026).
 * Kleuren: groen = ok, amber = krap (warning), rood = onder BKR (alert).
 */
function LiveBezettingBalk({ datum, initialBkr, onReload }) {
  const [bkr, setBkr] = useState(initialBkr || []);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => { setBkr(initialBkr || []); }, [initialBkr]);

  useEffect(() => {
    // Auto-refresh elke 30s — planning/day is licht (alleen bkr-veld)
    const iv = setInterval(async () => {
      try {
        const r = await api.get("/planning/day", { params: { datum } });
        setBkr(r.data?.bkr || []);
        setLastRefresh(new Date());
      } catch (_) { /* stil */ }
    }, 30000);
    return () => clearInterval(iv);
  }, [datum]);

  if (!bkr.length) return null;

  const totalKids = bkr.reduce((n, b) => n + (b.kinderen || 0), 0);
  const totalPmVereist = bkr.reduce((n, b) => n + (b.vereist || 0), 0);
  const totalPmActueel = bkr.reduce((n, b) => n + (b.medewerkers_ingeroosterd || 0), 0);
  const kritiek = bkr.filter(b => b.status === "alert").length;
  const warn = bkr.filter(b => b.status === "warning").length;
  const banner = kritiek ? "bg-red-50 border-red-300"
                : warn ? "bg-amber-50 border-amber-300"
                : "bg-emerald-50 border-emerald-300";

  return (
    <div className={`rounded-md border-2 ${banner} p-3 mb-4`} data-testid="live-bezetting-balk">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-widest text-neutral-700">
            Live bezetting
          </span>
          <span className="text-xs text-neutral-600">
            {totalKids} kind · {totalPmActueel}/{totalPmVereist} pm
          </span>
          {kritiek > 0 && (
            <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" data-testid="balk-kritiek">
              {kritiek} onder BKR
            </Badge>
          )}
          {warn > 0 && kritiek === 0 && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="balk-warn">
              {warn} krap
            </Badge>
          )}
          {kritiek === 0 && warn === 0 && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]" data-testid="balk-ok">
              alles op sterkte
            </Badge>
          )}
        </div>
        <button
          onClick={() => { onReload?.(); setLastRefresh(new Date()); }}
          className="text-[10px] text-neutral-500 hover:text-neutral-800 underline"
          title="Ververs handmatig"
          data-testid="balk-refresh"
        >
          {lastRefresh.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </button>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {bkr.map(b => {
          const tone = b.status === "ok" ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                    : b.status === "warning" ? "bg-amber-100 text-amber-900 border-amber-300"
                    : "bg-red-100 text-red-900 border-red-300";
          const ratio = b.vereist ? Math.min(100, (b.medewerkers_ingeroosterd / b.vereist) * 100) : 100;
          return (
            <div key={b.group_id}
                 data-testid={`balk-groep-${b.group_id}`}
                 className={`shrink-0 rounded border ${tone} px-2.5 py-1.5 min-w-[140px] relative overflow-hidden`}>
              <div className="text-[11px] font-semibold truncate">{b.name}</div>
              <div className="text-[10px] flex items-center justify-between mt-0.5">
                <span>{b.medewerkers_ingeroosterd}/{b.vereist} pm</span>
                <span className="opacity-75">{b.kinderen} k</span>
              </div>
              <div className="mt-1 h-1 bg-white/60 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all"
                     style={{
                       width: `${ratio}%`,
                       background: b.status === "ok" ? "#059669"
                                  : b.status === "warning" ? "#d97706"
                                  : "#dc2626",
                     }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
